import { memo, useMemo, CSSProperties } from 'react';
import { GridHeaderRow } from '../../../../../../api/generated/data-contracts';
import { VirtualItem } from '@tanstack/react-virtual';
import { GridHeader } from './Headers/GridHeader';
import { TableHeader } from './Headers/TableHeader';
import { BORDER, COLUMN_STICKY_COUNT, COLUMN_WIDTH, TOTAL_COLUMN_WIDTH, ROOT_ROW_BG, TOP_HEADERS_HEIGHT } from '../CONSTANTS';
import { useGridLayoutContext } from '../context/LayoutContext';
import { ColumnGridContainer } from './RowContainer/components/ColumnGridContainer';
import { Stack, Text } from '@fluentui/react';

export const HeaderContainer = memo(function HeaderContainer({
    start,
    totalWidth,
    headers,
    columns,
    gridName,
}: {
    start: number;
    columns: VirtualItem[];
    totalWidth: number;
    headers: GridHeaderRow[];
    gridName: string;
}) {
    const style = useMemo(() => {
        return {
            position: 'sticky',
            transform: `translateY(${start}px)`,
            top: 0,
            left: 0,
            height: `${TOP_HEADERS_HEIGHT}px`,
            borderBottom: BORDER,
            width: `${totalWidth}px`,
            boxSizing: 'border-box',
            background: ROOT_ROW_BG,
            zIndex: 2,
            display: 'flex',
        } as CSSProperties;
    }, [start, totalWidth]);

    const render = useMemo(() => {
        return columns.map(({ start, key, index }) => {
            if (index === 0) {
                return <GridHeader key={String(key)} gridName={gridName} />;
            }
            if (index === 1) {
                return <TotalHeader key={String(key)} />;
            }
            const columnIndex = index - COLUMN_STICKY_COUNT;
            const header = headers[columnIndex];
            return <Header key={String(key)} header={header} start={start} index={columnIndex} />;
        });
    }, [columns, gridName, headers]);
    return <div style={style}>{render}</div>;
});

const Header = ({ header: { name, subHeaderRows }, index, start }: { header: GridHeaderRow; index: number; start: number }) => {
    const style = useMemo(() => {
        return {
            width: `${COLUMN_WIDTH}px`,
            borderBottom: BORDER,
            borderRight: BORDER,
            zIndex: 1,
            position: 'absolute',
            transform: `translateX(${start}px)`,
            top: 0,
            left: 0,
            height: `${TOP_HEADERS_HEIGHT}px`,
            boxSizing: 'border-box',
            paddingTop: 20,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
        } as CSSProperties;
    }, [start]);
    return (
        <div style={style}>
            <TableHeader>{name}</TableHeader>
            <ColumnGridContainer>
                {subHeaderRows.map(subHeader => (
                    <Stack key={subHeader.name} horizontalAlign="center" horizontal>
                        <Text variant="small">{subHeader.name}</Text>
                    </Stack>
                ))}
            </ColumnGridContainer>
        </div>
    );
};

const TotalHeader = () => {
    const { rowHeaderSize } = useGridLayoutContext();
    const style = useMemo(() => {
        return {
            width: `${TOTAL_COLUMN_WIDTH}px`,
            borderBottom: BORDER,
            borderRight: BORDER,
            zIndex: 2,
            position: 'sticky',
            top: 0,
            left: rowHeaderSize,
            height: `${TOP_HEADERS_HEIGHT}px`,
            boxSizing: 'border-box',
            backgroundColor: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 20,
            justifyContent: 'space-between',
        } as CSSProperties;
    }, [rowHeaderSize]);
    return (
        <div style={style}>
            <TableHeader>
                Total
            </TableHeader>
            <Stack
                //
                horizontal
                tokens={{ padding: 10 }}
                horizontalAlign="space-evenly"
				styles={{root: {width: "100%"}}}
            >
                <Stack horizontalAlign="center" horizontal>
                    <Text variant="small">Request</Text>
                </Stack>
                <Stack horizontalAlign="center" horizontal>
                    <Text variant="small">Allocation</Text>
                </Stack>
            </Stack>
        </div>
    );
};
